import { Modal, Heading, Gap, FormItem, SelectMini, ISelectMiniOption } from '@salecycle/ui-component-library';
import moment from 'moment-timezone';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeOptions, ComparisonTypeOptions } from '../../data/ReportableDataConsts';
import { useReportableDataDispatch, useReportableDataState } from '../../context/UseReportableDataContext';
import { DashboardDateRange, ComparisonType } from '../../entities';
import { setDateRangeFilterAction } from '../../actions/filterActions';
import { setComparisonAction } from '../../actions/graphActions';
import CustomDateRange from '../CustomDateRange';
import { getDateRange, getStartAndEndDates, getComparisonStartAndEndDates, timeNow } from '../../utils/dates';
import usePrevious from 'core/ui/utils/usePrevious';

const getDateRangeSelectValue = (dateRangeOptions: ISelectMiniOption[], dateRange: DashboardDateRange) =>
  dateRangeOptions.find(dateRangeOption => dateRangeOption.value === String(dateRange));

const getComparisonTypeSelectValue = (comparisonTypeOptions: ISelectMiniOption[], comparisonType: ComparisonType) =>
  comparisonTypeOptions.find(comparisonTypeOption => comparisonTypeOption.value === String(comparisonType));

interface DateRangeModalProps {
  isOpen: boolean;
  toggleModal: () => void;
}

export const DateRangeModal: FC<DateRangeModalProps> = ({ isOpen, toggleModal }) => {
  const { t } = useTranslation('dashboard');
  const {
    queryParameters: { dateRange: initialDateRange, comparisonType: initialComparisonType },
    graphParameters: { timezone },
  } = useReportableDataState();
  const dispatch = useReportableDataDispatch();

  const [startDate, setStartDate] = useState<moment.Moment>(null);
  const [endDate, setEndDate] = useState<moment.Moment>(null);
  const [dateRangeSelectValue, setDateRangeSelectValue] = useState(
    getDateRangeSelectValue(DateRangeOptions(t), initialDateRange),
  );

  const [comparisonStartDate, setComparisonStartDate] = useState<moment.Moment>(null);
  const [comparisonEndDate, setComparisonEndDate] = useState<moment.Moment>(null);
  const [comparisonTypeSelectValue, setComparisonTypeSelectValue] = useState(
    getComparisonTypeSelectValue(ComparisonTypeOptions(t), initialComparisonType),
  );

  const { dateRange: primaryDateRange, dateRangeOffset: primaryDateRangeOffset } = getDateRange(
    startDate,
    endDate,
    timeNow(timezone),
  );
  const previousDateRange = usePrevious<number>(primaryDateRange);

  const handleDateRangeChanged = (option: ISelectMiniOption) => {
    setDateRangeSelectValue(option);
  };

  const handleCustomDateRangeClose = (startDate: moment.Moment, endDate: moment.Moment) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleCustomComparisonDateRangeClose = (startDate: moment.Moment, endDate: moment.Moment) => {
    setComparisonStartDate(startDate);
    setComparisonEndDate(endDate);
  };

  const handleComparisonTypeChanged = (option: ISelectMiniOption) => {
    setComparisonTypeSelectValue(option);
  };

  // When we close the modal we need to update the dashboard context with the newly selected date and comparison values
  const handleOkButtonClick = () => {
    // Update the date range
    const time = timeNow(timezone);
    if (dateRangeSelectValue.value === 'custom') {
      const { dateRange, dateRangeOffset } = getDateRange(startDate, endDate, time);
      setDateRangeFilterAction(dispatch, dateRange, dateRangeOffset);
    } else {
      setDateRangeFilterAction(dispatch, Number(dateRangeSelectValue.value));
    }

    const comparisonEnabled = comparisonTypeSelectValue.value !== ComparisonType.NONE;
    // Update the comparison
    const { dateRange, dateRangeOffset } = getDateRange(comparisonStartDate, comparisonEndDate, time);
    setComparisonAction(
      dispatch,
      comparisonEnabled,
      comparisonTypeSelectValue.value as ComparisonType,
      dateRange,
      dateRangeOffset,
    );

    // Close the modal
    toggleModal();
  };

  const isDateRangeValid = !!startDate && !!endDate;
  const isCustomDateRangeValid =
    comparisonTypeSelectValue.value !== 'custom' || (!!comparisonStartDate && !!comparisonEndDate);
  const allowCustomComparison =
    (isDateRangeValid && primaryDateRange <= 90) || Number(dateRangeSelectValue.value) <= 90;
  const comparisonDateRange = primaryDateRange === 0 ? 1 : primaryDateRange;

  const isComparisonDatesDisabled = comparisonTypeSelectValue.value === ComparisonType.CUSTOM && allowCustomComparison;

  // Remove the custom comparison if a custom comparison isn't allowed
  useEffect(() => {
    setComparisonStartDate(null);
    setComparisonEndDate(null);

    if (!allowCustomComparison && comparisonTypeSelectValue.value === ComparisonType.CUSTOM) {
      setComparisonTypeSelectValue(getComparisonTypeSelectValue(ComparisonTypeOptions(t), ComparisonType.NONE));
    }
  }, [t, allowCustomComparison, comparisonTypeSelectValue.value]);

  // Update the dates when the date range select is changed
  useEffect(() => {
    if (dateRangeSelectValue.value !== 'custom') {
      const { startDate, endDate } = getStartAndEndDates(Number(dateRangeSelectValue.value), 0, timeNow(timezone));

      setStartDate(startDate);
      setEndDate(endDate);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [dateRangeSelectValue, timezone]);

  // Update the comparison dates when the primary date is updated
  useEffect(() => {
    if (
      !isDateRangeValid ||
      (comparisonTypeSelectValue.value === ComparisonType.CUSTOM && previousDateRange !== primaryDateRange)
    ) {
      setComparisonStartDate(null);
      setComparisonEndDate(null);
    } else if (comparisonTypeSelectValue.value !== ComparisonType.CUSTOM) {
      const { startDate, endDate } = getComparisonStartAndEndDates(
        comparisonTypeSelectValue.value as ComparisonType,
        primaryDateRange,
        primaryDateRangeOffset,
        timeNow(timezone),
      );

      setComparisonStartDate(startDate);
      setComparisonEndDate(endDate);
    }
  }, [
    primaryDateRange,
    primaryDateRangeOffset,
    comparisonTypeSelectValue,
    timezone,
    isDateRangeValid,
    previousDateRange,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelButtonClicked={toggleModal}
      onCloseButtonClicked={toggleModal}
      onOkButtonClicked={handleOkButtonClick}
      okButtonLabel={t('dates.done')}
      heading={t('dates.heading')}
      variation="clean"
      overflow="visible"
      okButtonDisabled={!isDateRangeValid || !isCustomDateRangeValid}
    >
      <Heading variation="h3">{t('dates.dateHeading')}</Heading>
      <Gap top={20} />

      <div data-testid="DateRange">
        <FormItem isMini={true} spacingBelow={false} label={`${t('dates.date.label')}:`}>
          <SelectMini
            ariaLabel={t('dates.date.label')}
            onChange={handleDateRangeChanged}
            value={dateRangeSelectValue}
            options={DateRangeOptions(t)}
          />
        </FormItem>

        <Gap top={10} />

        <CustomDateRange
          onClose={handleCustomDateRangeClose}
          startDate={startDate}
          endDate={endDate}
          label={t('dates.dates.label')}
          changeStartDate={(date: moment.Moment) => setStartDate(date)}
          changeEndDate={(date: moment.Moment) => setEndDate(date)}
          isDisabled={dateRangeSelectValue.value !== 'custom'}
          endDateId="EndDate"
          startDateId="StartDate"
          alternativeFormat={true}
        />
      </div>

      <Gap top={40} />

      <Heading variation="h3">{t('dates.comparisonHeading')}</Heading>
      <Gap top={20} />

      <div data-testid="ComparisonDateRange">
        <FormItem isMini={true} spacingBelow={false} label={`${t('dates.comparison.label')}:`}>
          <SelectMini
            ariaLabel={t('dates.comparison.label')}
            onChange={handleComparisonTypeChanged}
            value={comparisonTypeSelectValue}
            options={
              allowCustomComparison
                ? ComparisonTypeOptions(t)
                : ComparisonTypeOptions(t).filter(option => option.value !== ComparisonType.CUSTOM)
            }
          />
        </FormItem>

        {comparisonTypeSelectValue.value !== ComparisonType.NONE && (
          <>
            <Gap top={10} />

            <CustomDateRange
              onClose={handleCustomComparisonDateRangeClose}
              startDate={comparisonStartDate}
              endDate={comparisonEndDate}
              dayRange={comparisonDateRange}
              isDisabled={!isComparisonDatesDisabled}
              label={t('dates.dates.label')}
              changeStartDate={(date: moment.Moment) => setComparisonStartDate(date)}
              changeEndDate={(date: moment.Moment) => setComparisonEndDate(date)}
              endDateId="ComparisonEndDate"
              startDateId="ComparisonStartDate"
              alternativeFormat={true}
            />
          </>
        )}
      </div>
    </Modal>
  );
};
